import { all, select, takeEvery, fork, call, put, delay } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import packageJson from '../../../../package.json'

// action
import {
	customerAuthActions,
	routeActions,
	authActions,
	userActions,
} from '@redux/store/index'

// lib
import { persistor } from '@redux/index'
import { Analytics, Logger } from '@utils'
import { Validator, ValidatorResultType } from '@validators'
import { POST_LOGIN_REDIRECT_KEY } from '@configs'

// api
import { customerApi, authApi, ResType, ParameterType } from '@api'

// schemas
import {
	Customer,
	CustomerType,
	User,
	AccessControl,
	AccessControlType,
	AccessControlDbType,
} from '@schemas'

// type
import type { CustomerAuthStateType } from '@redux/types'

const purgeStoredState = async (isRedirect: boolean): Promise<void> => {
	await persistor.purge().then(() => {
		if (isRedirect) {
			window.location.reload()
		}
	})
}

/**
 * 로그인·가입 뒤 갈 곳. 평소엔 홈이다. `/sso`(포럼 DiscourseConnect)가 로그인이 필요해서
 * 자기 주소를 적어 두고 로그인 페이지로 보낸 경우엔 거기로 되돌아가 핸드셰이크를 잇는다.
 * 사이트 안 경로만 받는다 — `//evil` 같은 것도 걸러야 오픈 리다이렉트가 안 된다.
 */
const consumePostLoginRedirect = (): string => {
	const next: string = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) ?? ''
	sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
	return next.startsWith('/') && !next.startsWith('//') ? next : '/'
}

const apiJoin = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await customerApi.join(data)
	return res
}

const apiLogin = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await customerApi.login(data)
	return res
}

/** 백오피스 계정(USERS). `/auth/login` 과 같은 엔드포인트다 */
const apiStaffLogin = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await authApi.login(data)
	return res
}

const apiForgotpassword = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await customerApi.forgotPassword(data)
	return res
}

const apiTokenValidation = async (accessToken: string): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }
	const res: ResType | undefined = await customerApi.tokenValidation({
		customHeaders: customHeaders,
	})
	return res
}

const apiUpdateProfileImage = async (
	file: File,
	accessToken: string,
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }

	const res: ResType | undefined = await customerApi.updateProfileImage({
		files: [file],
		customHeaders: customHeaders,
	})
	return res
}

const apiDeleteProfileImage = async (
	accessToken: string,
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }

	const res: ResType | undefined = await customerApi.deleteProfileImage({
		customHeaders: customHeaders,
	})
	return res
}

const apiUpdateCustomer = async (
	body: {
		id: string
		name: string
		email: string
	},
	accessToken: string,
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }

	const res: ResType | undefined = await customerApi.updateCustomer({
		body: body,
		customHeaders: customHeaders,
	})
	return res
}

const apiUpdateCustomerPassword = async (
	body: {
		id: string
		prevPassword: string
		newPassword: string
	},
	accessToken: string,
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }

	const res: ResType | undefined = await customerApi.updatePassword({
		body: body,
		customHeaders: customHeaders,
	})
	return res
}

const apiSendEmailVerificationCode = async (
	accessToken: string,
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }
	const res: ResType | undefined = await customerApi.sendEmailVerificationCode({
		customHeaders: customHeaders,
	})
	return res
}

const apiVerificationEmail = async (
	accessToken: string,
	body: {
		code: string
	},
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }
	const res: ResType | undefined = await customerApi.verificationEmail({
		customHeaders: customHeaders,
		body: body,
	})
	return res
}

/**
 * 스태프 로그인. 성공하면 `tryLoginSaga`(백오피스 로그인 화면)와 **같은 순서로** 상태를 세운다 —
 * 토큰(localStorage)·사용자·접근권한·isLogin. 하나라도 빠지면 반쪽 로그인이 되어
 * 다음 화면에서 튕긴다.
 */
function* runStaffLogin(email: string, password: string) {
	const apiRes: ResType = yield call(apiStaffLogin, { body: { email, password } })

	if (apiRes === undefined || apiRes.status !== 201) {
		return false
	}

	const data = apiRes.data[0] as any
	const accessControlsData: AccessControlDbType[] = data['accessControls'] ?? []
	const accessControls: AccessControlType[] = accessControlsData.map((anAccess: any) => {
		return new AccessControl({
			...anAccess,
			menuI18n: anAccess.menuI18n ?? [],
		})
	})

	yield put(
		authActions.TOKEN({
			accessToken: data.accessToken,
			refreshToken: data.refreshToken,
		}),
	)
	yield put(authActions.LOGIN_ERROR_MESSAGE(''))
	yield put(userActions.USER(new User({ ...data })))
	yield put(authActions.ACCESS_CONTROLS(accessControls))
	yield put(authActions.IS_LOGIN(true))

	Analytics.login()

	return true
}

function* loginSaga() {
	try {
		const loginInfo: CustomerAuthStateType['login'] = yield select(
			state => state.customerAuth.login,
		)

		const body = {
			email: loginInfo.email,
			password: loginInfo.password,
		}

		const apiRes: ResType = yield call(apiLogin, { body: { ...body } })

		if (apiRes.status !== 201) {
			/*
				고객 계정이 아니면 **스태프 계정으로 한 번 더** 물어본다. 이 화면은 사이트의
				유일한 로그인 문이고(포럼 `/sso` 도 여기로 보낸다), 스태프에게 백오피스
				로그인 화면(`/auth/login`)을 따로 찾아가게 할 이유가 없다.
				성공하면 `/auth/login` 으로 들어온 것과 똑같은 상태가 된다.
			*/
			const isStaff: boolean = yield call(runStaffLogin, body.email, body.password)

			if (isStaff) {
				yield put(customerAuthActions.LOGIN_ERROR_MESSAGE(''))
				yield put(routeActions.NAVIGATE_PATH(consumePostLoginRedirect()))
				return
			}

			const errorMessage = 'Please check your login information again.'
			yield put(customerAuthActions.LOGIN_ERROR_MESSAGE(errorMessage))
		} else {
			const data = apiRes.data[0] as any
			const newCustomer = new Customer({ ...data })
			yield put(customerAuthActions.CUSTOMER(newCustomer))
			yield put(customerAuthActions.IS_LOGEDIN(true))
			yield put(customerAuthActions.LOGIN_ERROR_MESSAGE(''))
			Analytics.login()

			sessionStorage.setItem('accessCustomerToken', newCustomer.accessToken)
			sessionStorage.setItem('refreshCustomerToken', newCustomer.refreshToken)

			yield put(routeActions.NAVIGATE_PATH(consumePostLoginRedirect()))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* joinSaga() {
	try {
		const joinInfo: CustomerAuthStateType['join'] = yield select(
			state => state.customerAuth.join,
		)

		const res: ValidatorResultType = Validator.customer({
			name: joinInfo.name,
			email: joinInfo.email,
			password: joinInfo.password,
			confirmPassword: joinInfo.confirmPassword,
		})

		if (!res.name.isValid) {
			yield put(customerAuthActions.JOIN_ERROR_MESSAGE(res.name.message))
		} else if (!res.email.isValid) {
			yield put(customerAuthActions.JOIN_ERROR_MESSAGE(res.email.message))
		} else if (!res.password.isValid) {
			yield put(customerAuthActions.JOIN_ERROR_MESSAGE(res.password.message))
		} else if (!res.confirmPassword.isValid) {
			yield put(customerAuthActions.JOIN_ERROR_MESSAGE(res.confirmPassword.message))
		} else {
			const body = {
				name: joinInfo.name,
				email: joinInfo.email,
				confirmEmailNewsYn: joinInfo.confirmEmailNewsYn ? 1 : 0,
				password: joinInfo.password,
			}

			const apiRes: ResType = yield call(apiJoin, { body: { ...body } })

			if (apiRes.status !== 201) {
				const errorMessage =
					apiRes.exception.message.meta.detail.title ===
					'[CUSTOMER-SERVICE]: Join error - Duplicated email'
						? 'This email has been duplicated.'
						: apiRes.exception.message.meta.detail.title
				yield put(customerAuthActions.JOIN_ERROR_MESSAGE(errorMessage))
			} else {
				const data = apiRes.data[0] as any
				const newCustomer = new Customer({ ...data })
				yield put(customerAuthActions.CUSTOMER(newCustomer))
				yield put(customerAuthActions.IS_LOGEDIN(true))
				Analytics.signUp()

				sessionStorage.setItem('accessCustomerToken', newCustomer.accessToken)
				sessionStorage.setItem('refreshCustomerToken', newCustomer.refreshToken)

				yield put(routeActions.NAVIGATE_PATH(consumePostLoginRedirect()))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* forgotPwSaga() {
	try {
		const forgotPwInfo: CustomerAuthStateType['forgotPassword'] = yield select(
			state => state.customerAuth.forgotPassword,
		)

		if (forgotPwInfo.email !== '') {
			const body = {
				email: forgotPwInfo.email,
			}
			const apiRes: ResType = yield call(apiForgotpassword, { body: { ...body } })

			if (apiRes.status !== 201) {
				const errorMessage =
					apiRes.exception.message.meta.detail.title === '[CUSTOMER-SERVICE]: No Customer'
						? 'There are no customers using this email.'
						: apiRes.exception.message.meta.detail.title

				yield put(customerAuthActions.FORGOT_PASSWORD_ERROR_MESSAGE(errorMessage))
			} else {
				yield put(customerAuthActions.FORGOT_PASSWORD_ERROR_MESSAGE(''))
				yield put(customerAuthActions.SHOW_FORGOT_PASSWORD_MODAL(true))
				yield put(customerAuthActions.FORGOT_PASSWORD({ email: '' }))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerTokenValidationSaga() {
	try {
		const localVersion = localStorage.getItem('systemVersion')
		if (localVersion === null || packageJson.version !== localVersion) {
			localStorage.setItem('systemVersion', packageJson.version)
			yield call(purgeStoredState, true)
		}

		const isLogedin: boolean = yield select(state => state.customerAuth.isLogedin)
		const customer: CustomerType = yield select(state => state.customerAuth.customer)
		const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')

		if (sessionAccessToken === null || sessionAccessToken === '' || !isLogedin) {
			yield put(customerAuthActions.CUSTOMER(new Customer()))
			sessionStorage.setItem('accessCustomerToken', '')
			sessionStorage.setItem('refreshCustomerToken', '')

			yield put(customerAuthActions.IS_LOGEDIN(false))
			yield put(customerAuthActions.IS_LOADING(false))
		} else if ((sessionAccessToken !== null || sessionAccessToken !== '') && isLogedin) {
			const apiRes: ResType = yield call(apiTokenValidation, sessionAccessToken ?? '')

			if (apiRes.status !== 201) {
				if (customer.id !== '') {
					yield put(customerAuthActions.CUSTOMER(new Customer()))
				}

				if (sessionAccessToken !== null || sessionAccessToken !== '') {
					sessionStorage.setItem('accessCustomerToken', '')
					sessionStorage.setItem('refreshCustomerToken', '')
				}

				if (isLogedin) {
					yield put(customerAuthActions.IS_LOGEDIN(false))
				}

				yield put(customerAuthActions.IS_LOADING(false))
			} else {
				const data = apiRes.data[0] as any
				const newCustomer = new Customer({ ...data })

				if (JSON.stringify(newCustomer) !== JSON.stringify(customer)) {
					yield put(customerAuthActions.CUSTOMER(newCustomer))
				}

				if (!isLogedin) {
					yield put(customerAuthActions.IS_LOGEDIN(true))
				}

				yield put(customerAuthActions.IS_LOADING(false))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerLogoutSaga() {
	try {
		sessionStorage.setItem('accessCustomerToken', '')
		sessionStorage.setItem('refreshCustomerToken', '')

		yield put(customerAuthActions.IS_LOGEDIN(false))
		yield put(customerAuthActions.CUSTOMER(new Customer()))
		// 저장소를 비운 뒤 **홈으로** 전체 로드한다. 예전엔 현재 주소로 reload 해서
		// /customer/logout 에 그대로 남았다 — 로그아웃 페이지가 다시 뜨는 것은 끝난 게 아니다.
		yield call(purgeStoredState, false)
		window.location.replace('/')
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerSendEmailVerificationCodeSaga() {
	try {
		const isLogedin: boolean = yield select(state => state.customerAuth.isLogedin)
		const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')

		if (isLogedin && (sessionAccessToken !== null || sessionAccessToken !== '')) {
			const apiRes: ResType = yield call(
				apiSendEmailVerificationCode,
				sessionAccessToken ?? '',
			)

			if (apiRes.status === 201) {
				yield put(
					customerAuthActions.CUSTOMER_INFO_MODAL_INFO({
						target: 'verificationCodeSent',
						title: 'Verification code sent',
					}),
				)
				yield put(customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(true))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateCustomerSaga() {
	try {
		const isLogedin: boolean = yield select(state => state.customerAuth.isLogedin)
		const customer: CustomerType = yield select(state => state.customerAuth.customer)
		const updateCustomer: CustomerAuthStateType['updateCustomer'] = yield select(
			state => state.customerAuth.updateCustomer,
		)
		const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')

		if (isLogedin && sessionAccessToken !== null && sessionAccessToken !== '') {
			const body = {
				id: customer.id,
				name: updateCustomer.name,
				email: updateCustomer.email,
				confirmEmailNewsYn: updateCustomer.confirmEmailNewsYn,
			}

			const apiRes: ResType = yield call(apiUpdateCustomer, body, sessionAccessToken)

			if (apiRes.status === 201) {
				// 이메일이 바뀌면 서버가 VERIFIED_YN 을 0 으로 되돌린다. 이 값을 반영하지
				// 않으면 실제로는 미인증인데 화면에 Verified 가 남는다.
				const isEmailChanged: boolean = customer.email !== updateCustomer.email

				yield put(
					customerAuthActions.CUSTOMER({
						...customer,
						...updateCustomer,
						verifiedYn: isEmailChanged ? 0 : customer.verifiedYn,
					}),
				)
				yield put(customerAuthActions.UPDATE_CUSTOMER_ERROR_MESSAGE(''))
				yield put(customerAuthActions.IS_EDIT_CUSTOMER_INFO(false))

				if (isEmailChanged) {
					// 새 주소로 인증 코드를 바로 보낸다 (서버는 DB 의 현재 이메일로 발송한다).
					// Judahs Studio 의 이메일 변경 흐름과 동일하다.
					yield put(customerAuthActions.TRIGGER_SEND_EMAIL_VERIFICATION_CODE(true))
				} else {
					yield put(
						customerAuthActions.CUSTOMER_INFO_MODAL_INFO({
							target: 'updateSuccess',
							title: 'Update Success',
						}),
					)
					yield put(customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(true))
				}
			} else {
				const errorMessage = apiRes.exception.message.meta.detail.title
				yield put(customerAuthActions.UPDATE_CUSTOMER_ERROR_MESSAGE(errorMessage))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updatePasswordSaga() {
	try {
		const isLogedin: boolean = yield select(state => state.customerAuth.isLogedin)
		const customer: CustomerType = yield select(state => state.customerAuth.customer)
		const updatePassword: CustomerAuthStateType['updatePassword'] = yield select(
			state => state.customerAuth.updatePassword,
		)

		if (
			updatePassword.confirmPassword === '' &&
			updatePassword.newPassword === '' &&
			updatePassword.confirmPassword === ''
		) {
			yield put(customerAuthActions.UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE(''))
		} else {
			const res: ValidatorResultType = Validator.customer({
				password: updatePassword.newPassword,
				confirmPassword: updatePassword.confirmPassword,
			})

			if (!res.password.isValid) {
				yield put(
					customerAuthActions.UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE(
						res.password.message,
					),
				)
			} else if (!res.confirmPassword.isValid) {
				yield put(
					customerAuthActions.UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE(
						res.confirmPassword.message,
					),
				)
			} else {
				const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')

				if (isLogedin && sessionAccessToken !== null && sessionAccessToken !== '') {
					const body = {
						id: customer.id,
						prevPassword: updatePassword.prevPassword,
						newPassword: updatePassword.newPassword,
					}

					const apiRes: ResType = yield call(
						apiUpdateCustomerPassword,
						body,
						sessionAccessToken,
					)

					if (apiRes.status !== 201) {
						const errorMessage =
							apiRes.exception.message.meta.detail.title ===
							'[CUSTOMER-SERVICE]:Wrong Password'
								? 'Current Password is incorrect.'
								: apiRes.exception.message.meta.detail.title

						yield put(
							customerAuthActions.UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE(errorMessage),
						)
					} else {
						yield put(
							customerAuthActions.UPDATE_PASSWORD({
								prevPassword: '',
								newPassword: '',
								confirmPassword: '',
							}),
						)
						yield put(customerAuthActions.SHOW_CUSTOMER_PASSWORD_MODAL(true))
					}
				}
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* verificationEmailSaga() {
	try {
		const isLogedin: boolean = yield select(state => state.customerAuth.isLogedin)
		const verificationEmail: CustomerAuthStateType['verificationEmail'] = yield select(
			state => state.customerAuth.verificationEmail,
		)
		const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')

		if (isLogedin && (sessionAccessToken !== null || sessionAccessToken !== '')) {
			const body = {
				code: verificationEmail.code,
			}
			const apiRes: ResType = yield call(
				apiVerificationEmail,
				sessionAccessToken ?? '',
				body,
			)

			if (apiRes.status === 201) {
				yield put(
					customerAuthActions.CUSTOMER_INFO_MODAL_INFO({
						target: 'verificationSuccess',
						title: 'Verification Success',
					}),
				)
				yield put(customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(true))
				yield put(customerAuthActions.TRIGGER_TOKEN_VALIDATION(true))
			} else {
				yield put(
					customerAuthActions.CUSTOMER_INFO_MODAL_INFO({
						target: 'verificationFailed',
						title: 'Verification Failed',
					}),
				)
				yield put(customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(true))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/**
 * 아바타 업로드.
 *
 * 서버가 거부하면 그 사유를 그대로 화면에 올린다 — 정책 위반(422)과 통신 오류를
 * 같은 문구로 뭉개면 사용자는 무엇을 고쳐야 할지 알 수 없다.
 */
function* uploadProfileImageSaga(action: PayloadAction<{ file: File }>) {
	try {
		const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')
		const customer: CustomerType = yield select(state => state.customerAuth.customer)

		if (sessionAccessToken === null || sessionAccessToken === '') {
			return
		}

		yield put(customerAuthActions.IS_UPLOADING_PROFILE_IMAGE(true))
		yield put(customerAuthActions.PROFILE_IMAGE_ERROR_MESSAGE(''))

		const apiRes: ResType = yield call(
			apiUpdateProfileImage,
			action.payload.file,
			sessionAccessToken,
		)

		if (apiRes !== undefined && [200, 201].includes(apiRes.status)) {
			const data = apiRes.data[0] as any
			yield put(customerAuthActions.CUSTOMER(new Customer({ ...customer, ...data })))
			yield put(customerAuthActions.SHOW_PROFILE_IMAGE_MODAL(false))
		} else {
			yield put(
				customerAuthActions.PROFILE_IMAGE_ERROR_MESSAGE(
					getProfileImageErrorMessage(apiRes),
				),
			)
		}

		yield put(customerAuthActions.IS_UPLOADING_PROFILE_IMAGE(false))
	} catch (error) {
		Logger.warn(error)
		yield put(
			customerAuthActions.PROFILE_IMAGE_ERROR_MESSAGE(
				'The image could not be uploaded. Please try again.',
			),
		)
		yield put(customerAuthActions.IS_UPLOADING_PROFILE_IMAGE(false))
	}
}

/** 서버 상태코드를 사람이 읽을 문장으로. 사유가 다르면 문장도 달라야 한다 */
const getProfileImageErrorMessage = (apiRes: ResType | undefined): string => {
	const status: number = apiRes?.status ?? 0

	if (status === 422) {
		return 'This image does not meet our profile picture policy. Please choose another image.'
	}
	if (status === 429) {
		return 'You have changed your picture too many times today. Please try again tomorrow.'
	}
	if (status === 403) {
		return 'Verify your email address before setting a profile picture.'
	}
	if (status === 413) {
		return 'This file is too large. The limit is 8MB.'
	}
	if (status === 400) {
		return 'This file is not a readable image.'
	}

	return 'The image could not be uploaded. Please try again.'
}

function* deleteProfileImageSaga() {
	try {
		const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')
		const customer: CustomerType = yield select(state => state.customerAuth.customer)

		if (sessionAccessToken === null || sessionAccessToken === '') {
			return
		}

		yield put(customerAuthActions.IS_UPLOADING_PROFILE_IMAGE(true))

		const apiRes: ResType = yield call(apiDeleteProfileImage, sessionAccessToken)

		if (apiRes !== undefined && [200, 201].includes(apiRes.status)) {
			const data = apiRes.data[0] as any
			yield put(customerAuthActions.CUSTOMER(new Customer({ ...customer, ...data })))
			yield put(customerAuthActions.SHOW_PROFILE_IMAGE_MODAL(false))
			yield put(customerAuthActions.PROFILE_IMAGE_ERROR_MESSAGE(''))
		}

		yield put(customerAuthActions.IS_UPLOADING_PROFILE_IMAGE(false))
	} catch (error) {
		Logger.warn(error)
		yield put(customerAuthActions.IS_UPLOADING_PROFILE_IMAGE(false))
	}
}

function* mainSaga() {
	yield takeEvery(
		customerAuthActions.TRIGGER_TOKEN_VALIDATION,
		triggerTokenValidationSaga,
	)
	yield takeEvery(customerAuthActions.TRIGGER_LOGOUT, triggerLogoutSaga)
	yield takeEvery(
		customerAuthActions.TRIGGER_SEND_EMAIL_VERIFICATION_CODE,
		triggerSendEmailVerificationCodeSaga,
	)

	yield takeEvery(customerAuthActions.LOGIN, loginSaga)
	yield takeEvery(customerAuthActions.JOIN, joinSaga)
	yield takeEvery(customerAuthActions.FORGOT_PASSWORD, forgotPwSaga)

	yield takeEvery(customerAuthActions.UPDATE_CUSTOMER, updateCustomerSaga)
	yield takeEvery(customerAuthActions.UPDATE_PASSWORD, updatePasswordSaga)

	yield takeEvery(customerAuthActions.VERIFICATION_EMAIL, verificationEmailSaga)

	yield takeEvery(customerAuthActions.UPLOAD_PROFILE_IMAGE, uploadProfileImageSaga)
	yield takeEvery(customerAuthActions.DELETE_PROFILE_IMAGE, deleteProfileImageSaga)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
